import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconDiamond, IconGift, IconShare } from '@tabler/icons-react';
import { functions, storage } from '../firebase';
import { httpsCallable } from "firebase/functions";
import { ref, getDownloadURL } from 'firebase/storage';
import EarnDiamondsChecklist from '../components/EarnDiamondsChecklist';
import ReferralInvitesList from '../components/ReferralInvitesList';
import Footer from '../components/Footer';
import './DiamondsPage.css';

function DiamondsPage({ user, onCreditsUpdated }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [monthlyUsage, setMonthlyUsage] = useState({ currentCount: 0, monthlyLimit: 10, remaining: 10, bonusCredits: 0 });
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  
  // State for watermarked generations
  const [watermarkedGenerations, setWatermarkedGenerations] = useState([]);
  const [isLoadingWatermarked, setIsLoadingWatermarked] = useState(true);
  
  // State for published generations (earned from publishing to feed)
  const [publishedGenerations, setPublishedGenerations] = useState([]);
  const [isLoadingPublished, setIsLoadingPublished] = useState(true);

  useEffect(() => {
    if (user) {
      loadMonthlyUsage();
      loadWatermarkedGenerations();
      loadPublishedGenerations();
    } else {
      setIsLoadingUsage(false);
      setIsLoadingWatermarked(false);
      setIsLoadingPublished(false);
    }
  }, [user]);

  const loadMonthlyUsage = async () => {
    if (!user) return;

    setIsLoadingUsage(true);
    try {
      const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
      const result = await getUserMonthlyUsageFunc();
      setMonthlyUsage(result.data);
      // Always update the parent component's credits when loaded
      if (onCreditsUpdated) {
        onCreditsUpdated(result.data);
      }
    } catch (error) {
      console.error("Error loading monthly usage:", error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const loadWatermarkedGenerations = async () => {
    if (!user) return;

    setIsLoadingWatermarked(true);
    try {
      const getWatermarkedFunc = httpsCallable(functions, 'getWatermarkedGenerations');
      const result = await getWatermarkedFunc({ limit: 10 });
      setWatermarkedGenerations(result.data.generations || []);
    } catch (error) {
      console.error("Error loading watermarked generations:", error);
    } finally {
      setIsLoadingWatermarked(false);
    }
  };

  const loadPublishedGenerations = async () => {
    if (!user) return;

    setIsLoadingPublished(true);
    try {
      // Use existing getUserPublicMaps function
      // Public maps = maps that were published = maps that earned diamonds!
      const getUserMaps = httpsCallable(functions, 'getUserPublicMaps');
      const result = await getUserMaps({ 
        userId: user.uid, 
        includePrivate: false, // Only public maps = only published maps
        limit: 10
      });
      
      // Convert storage paths to download URLs (same as ProfilePage)
      const publishedMaps = await Promise.all(
        (result.data.maps || []).map(async (map) => {
          let imageUrl = null;
          const imagePath = map.generatedImagePath || map.originalImagePath;
          
          if (imagePath) {
            try {
              imageUrl = await getDownloadURL(ref(storage, imagePath));
            } catch (e) {
              console.warn(`Failed to get image URL for ${imagePath}:`, e.message);
            }
          }
          
          return {
            id: map.id,
            activityName: map.activityName || 'Untitled Map',
            generatedImageUrl: imageUrl,
            creditAwarded: true // All public maps earned publish diamond
          };
        })
      );
      
      setPublishedGenerations(publishedMaps);
    } catch (error) {
      console.error("Error loading published generations:", error);
    } finally {
      setIsLoadingPublished(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!user) {
    return (
      <div className="diamonds-page">
        <div className="diamonds-not-logged-in">
          <h2>{t('diamondsPage.signInRequired', 'Sign in required')}</h2>
          <p>{t('diamondsPage.signInToView', 'Please sign in to view your diamonds.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="diamonds-page">
      <div className="diamonds-container">
        <div className="diamonds-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            {t('diamondsPage.back', 'Back')}
          </button>
          <h1>💎 {t('diamondsPage.title', 'Diamonds')}</h1>
          <p className="diamonds-subtitle">{t('diamondsPage.subtitle', 'Earn diamonds through rewards')}</p>
        </div>
        <div className="diamonds-divider"></div>

        {/* Current Diamonds Section */}
        <div className="diamonds-section">
          <h2>
            <IconDiamond size={24} style={{ color: '#8b9dc3' }} />
            {t('diamondsPage.yourDiamonds', 'Your Diamonds')}
          </h2>
          <p className="section-description">{t('diamondsPage.trackDiamonds', 'Track your available diamonds for creating mythic maps')}</p>

          <div className="diamonds-counter">
            <div className="diamonds-info">
              <span className="diamonds-badge">{t('diamondsPage.diamonds', 'Diamonds')}</span>
            </div>
            <div className="diamonds-count">
              {isLoadingUsage ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <>
                  <span className="current">{monthlyUsage.remaining}</span>
                  <span className="separator">{t('diamondsPage.remaining', 'remaining')}</span>
                  {monthlyUsage.bonusCredits > 0 && (
                    <span className="bonus-diamonds">
                      {t('diamondsPage.includesEarned', '(includes {{count}} earned)', { count: monthlyUsage.bonusCredits })}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Earn Diamonds Section */}
        <div className="diamonds-section">
          <h2>
            <IconGift size={24} style={{ color: '#8b9dc3' }} />
            {t('diamondsPage.earnDiamonds', 'Earn Diamonds')}
          </h2>
          <p className="section-description">{t('diamondsPage.earnDiamondsDesc', 'Complete these activities to earn more diamonds')}</p>

          {/* Activation cluster: Email + Strava Club + referral, plus a Challenges
             card once both starters are claimed. The hub leaves claimed cards
             visible (claimed state); the page already supplies the heading. */}
          <EarnDiamondsChecklist
            user={user}
            onRewardClaimed={loadMonthlyUsage}
            showHeader={false}
          />

          {/* The inviter's own referral invites: status per invite, with the +5 granted
             server-side when a referral map completes. */}
          <ReferralInvitesList user={user} onCreditsUpdated={loadMonthlyUsage} />

          {/* Publish to Feed and Earn */}
          <div className="reward-card">
            <div className="reward-content">
              <div className="reward-header">
                <h4>
                  <IconShare size={20} style={{ marginRight: '8px' }} />
                  {t('diamondsPage.publishAndEarn', 'Publish to Feed and Earn')}
                </h4>
                <span className="reward-value">+1 💎</span>
              </div>
              <p>{t('diamondsPage.publishAndEarnDesc', 'Manually publish your mythic maps to the Mythic Maps feed and earn 1 diamond per map, up to your monthly limit. Maps that auto-uploaded to Strava count too, as long as you publish them to the feed here yourself.')}</p>

              <div className="share-info-box">
                <p>
                  <strong>{t('diamondsPage.howItWorks', 'How it works:')}</strong> {t('diamondsPage.publishHowItWorksDesc', 'Create a map, publish it to the Mythic Maps feed, and earn 1 diamond automatically. Only your first publication of each map earns a diamond.')}
                </p>
              </div>

              {/* Maps where diamonds were earned from publishing */}
              {isLoadingPublished ? (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <div className="loading-spinner-small" style={{ margin: '0 auto' }}></div>
                </div>
              ) : publishedGenerations.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h5 style={{ margin: '0 0 12px 0', color: '#4a4a4a', fontSize: '15px', fontWeight: '600' }}>
                    {t('diamondsPage.mapsPublished', "Maps you've published to the feed:")}
                  </h5>
                  <div className="earned-maps-grid">
                    {publishedGenerations.map((generation) => (
                      <div key={generation.id} className="earned-map-card">
                        {generation.generatedImageUrl && (
                          <div className="map-thumbnail">
                            <img
                              src={generation.generatedImageUrl}
                              alt={generation.activityName}
                            />
                            {generation.creditAwarded && (
                              <div className="diamond-earned-badge">
                                💎 +1
                              </div>
                            )}
                          </div>
                        )}
                        <div className="map-info">
                          <h5>{generation.activityName}</h5>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Share to Strava and Earn */}
          <div className="reward-card">
            <div className="reward-content">
              <div className="reward-header">
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px', color: '#FC5200' }}>
                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                  </svg>
                  {t('diamondsPage.shareToStravaAndEarn', 'Share to Strava and Earn')}
                </h4>
                <span className="reward-value">+1 💎</span>
              </div>
              <p>{t('diamondsPage.shareToStravaDesc', 'Manually share your mythic maps to Strava from the app and earn 1 diamond per map, up to your monthly limit. Maps that auto-upload to Strava on their own do not earn a diamond, only the ones you share yourself.')}</p>

              <div className="share-info-box">
                <p>
                  <strong>{t('diamondsPage.howItWorks', 'How it works:')}</strong> {t('diamondsPage.howItWorksDesc', 'Create a map, share it on Strava, and earn 1 diamond automatically.')}
                </p>
              </div>

              {/* Maps where diamonds were earned */}
              {isLoadingWatermarked ? (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <div className="loading-spinner-small" style={{ margin: '0 auto' }}></div>
                </div>
              ) : watermarkedGenerations.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h5 style={{ margin: '0 0 12px 0', color: '#4a4a4a', fontSize: '15px', fontWeight: '600' }}>
                    {t('diamondsPage.mapsEarnedFor', "Maps you've earned diamonds for:")}
                  </h5>
                  <div className="earned-maps-grid">
                    {watermarkedGenerations.map((generation) => (
                      <div key={generation.id} className="earned-map-card">
                        {generation.generatedImageUrl && (
                          <div className="map-thumbnail">
                            <img
                              src={generation.generatedImageUrl}
                              alt={generation.activityName}
                            />
                            {generation.creditAwarded && (
                              <div className="diamond-earned-badge">
                                💎 +1
                              </div>
                            )}
                          </div>
                        )}
                        <div className="map-info">
                          <h5>{generation.activityName}</h5>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}

export default DiamondsPage;
